import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ScheduleService } from 'src/app/services/schedule.service';

type Vista = 'agenda' | 'form';

@Component({
  selector: 'app-agenda-doctor',
  templateUrl: './agenda-doctor.component.html',
  styleUrls: ['./agenda-doctor.component.css']
})
export class AgendaDoctorComponent implements OnInit {
  vista: Vista = 'agenda';
  @Input() consulta: any = {};
  @Input() selectedView: string = '';

  horas_del_dia: any[] = [];

  selectedDate: string = this.toISODate(new Date());
  intervaloMin = 30;

  isLoading: boolean = true;

  // [{hora:'23:00', horaFin:'23:30', cita:{...} | null, sch_is_ondemand:boolean, originalSlots?:[]}]
  slots: any[] = [];

  // ✅ SOLO 1 slot (siguiente bloque) pero ya mergeado
  hora_siguiente: any[] = [];

  slotSeleccionado: any = null;
  formAgendar!: FormGroup;

  constructor(
    private fb: FormBuilder,
    public scheduleService: ScheduleService
  ) { }

  ngOnInit(): void {
    this.formAgendar = this.fb.group({
      paciente: ['', [Validators.required, Validators.minLength(3)]],
      motivo: ['', [Validators.required, Validators.minLength(5)]],
      telefono: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
    });

    this.cargarAgendaDelDia();
  }

  onFechaChange(): void {
    this.cargarAgendaDelDia();
  }

  private cargarAgendaDelDia(): void {
    this.isLoading = true;

    const doctorId =
      this.consulta?.doc_id ?? this.consulta?.doctorId ?? this.consulta?.id;

    this.scheduleService.postListarAgendaMedicoDia(doctorId, this.selectedDate)
      .then((res: any[]) => {
        this.horas_del_dia = Array.isArray(res) ? res : [];
        // console.log(this.horas_del_dia);
        
        this.generarSlotsDesdeBackend();
        this.calcularHoraSiguiente();

        this.isLoading = false;
      })
      .catch(err => {
        console.error('postListarAgendaMedicoDia error', err);
        this.horas_del_dia = [];
        this.slots = [];
        this.hora_siguiente = [];
        this.isLoading = false;
      });
  }

  // =========================
  // 1) Construye slots 1:1
  // 2) Ordena
  // 3) Merge de rangos ondemand disponibles
  // =========================
  private generarSlotsDesdeBackend(): void {
    const rawSlots = this.horas_del_dia.map(item => {
      const hora = (item?.sch_hou_from || '').substring(0, 5);
      const horaFin = (item?.sch_hou_to || '').substring(0, 5);

      // Normaliza datosPacienteConsulta (a veces viene array)
      const dpRaw = item?.datosPacienteConsulta;
      const dp = Array.isArray(dpRaw) ? (dpRaw[0] || null) : (dpRaw || null);

      const estado = (item?.sta_name || '').toUpperCase();

      // Si está DISPONIBLE (o viene null) => slot sin cita
      const esDisponible = !item?.sta_name || estado === 'DISPONIBLE';


      const cita = esDisponible ? null : {
        id: item?.sch_hou_id,
        fecha: this.selectedDate,
        hora_inicio: hora,
        hora_fin: horaFin,
        estado: item?.sta_name,
        esOnDemand: !!item?.sch_is_ondemand,
        datosPacienteConsulta: {
          use_name: dp?.use_name,
          use_paternal_surname: dp?.use_paternal_surname,
          spe_name: dp?.spe_name,
          tel_doc_app_boo_is_ondemand: dp?.tel_doc_app_boo_is_ondemand
        }
      };
      // console.log(cita);
      
      return {
        hora,
        horaFin,
        cita,
        sch_is_ondemand: !!item?.sch_is_ondemand,
        sta_name: item?.sta_name || null
      };

    });

    // ordenar por hora inicio
    rawSlots.sort((a, b) => this.hhmmToMinutes(a?.hora) - this.hhmmToMinutes(b?.hora));

    // ✅ merge rangos ondemand disponibles ininterrumpidos
    this.slots = this.mergeOndemandRanges(rawSlots);

    // (debug opcional)
    // console.log('SLOTS MERGED:', this.slots.map(s => ({
    //   hora: s.hora, horaFin: s.horaFin, ondemand: s.sch_is_ondemand, tieneCita: !!s.cita, n: s.originalSlots?.length || 1
    // })));
  }

  // =========================
  // Merge ONDEMAND disponibles por continuidad (horaFin == siguiente.hora)
  // =========================
  private mergeOndemandRanges(slots: any[]): any[] {
    if (!Array.isArray(slots) || slots.length === 0) return [];

    const out: any[] = [];
    let i = 0;

    while (i < slots.length) {
      const curr = slots[i];

      const isOndemand = !!curr?.sch_is_ondemand;
      if (!isOndemand) {
        out.push(curr);
        i++;
        continue;
      }

      let startMin = this.hhmmToMinutes(curr.hora);
      let endMin = this.hhmmToMinutes(curr.horaFin);

      const originalSlots = [curr];

      while (i + 1 < slots.length) {
        const next = slots[i + 1];

        // solo unir si el siguiente también es ondemand
        if (!next?.sch_is_ondemand) break;

        const nextStartMin = this.hhmmToMinutes(next.hora);
        if (nextStartMin !== endMin) break;

        i++;
        endMin = this.hhmmToMinutes(slots[i].horaFin);
        originalSlots.push(slots[i]);
      }

      // 🔥 slot unificado
      out.push({
        ...curr,
        hora: this.minutesToHHMM(startMin),
        horaFin: this.minutesToHHMM(endMin),
        sch_is_ondemand: true,
        // lo dejamos "disponible" visualmente (no cita)
        cita: null,
        // por si quieres mostrar estado/badge
        sta_name: 'A. INMEDIATA',
        originalSlots
      });

      i++;
    }

    return out;
  }


  // =========================
  // Hora siguiente (tomada desde slots ya mergeados)
  // =========================
  private calcularHoraSiguiente(): void {
    this.hora_siguiente = [];

    if (!this.slots || this.slots.length === 0) return;

    const fc = this.consulta?.fecha_consulta;
    const baseTime = fc?.time; // ej "23:00"
    if (!baseTime) return;

    const horaObjetivo = this.addMinutesToHHMM(baseTime, this.intervaloMin);

    const match = this.slots
      .filter(s => (s.hora || '').localeCompare(horaObjetivo) >= 0)
      .find(s => (s?.cita?.estado || '').toUpperCase() !== 'FINALIZADA');

    this.hora_siguiente = match ? [match] : [];
  }

  // =========================
  // Formulario
  // =========================
  iniciarAgendar(slot: any): void {
    // si viene rango merged, por defecto toma el primer bloque real
    // (ajusta esto si quieres agendar el rango completo)
    if (slot?.originalSlots?.length) {
      this.slotSeleccionado = slot.originalSlots[0];
    } else {
      this.slotSeleccionado = slot;
    }

    this.formAgendar.reset();
    this.vista = 'form';
  }

  volverAAgenda(): void {
    this.vista = 'agenda';
    this.slotSeleccionado = null;
  }

  guardarAgendamiento(): void {
    if (!this.slotSeleccionado) return;

    if (this.formAgendar.invalid) {
      this.formAgendar.markAllAsTouched();
      return;
    }

    // aquí iría tu lógica real de guardado

    this.volverAAgenda();
    this.cargarAgendaDelDia();
  }

  // =========================
  // Utils
  // =========================
  private hhmmToMinutes(hhmm: string): number {
    const t = (hhmm || '').substring(0, 5);
    const parts = t.split(':');
    if (parts.length < 2) return -1;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return -1;
    return (h * 60) + m;
  }

  private minutesToHHMM(min: number): string {
    const m = ((min % (24 * 60)) + (24 * 60)) % (24 * 60);
    const hh = Math.floor(m / 60);
    const mm = m % 60;
    return `${this.pad2(hh)}:${this.pad2(mm)}`;
  }

  private addMinutesToHHMM(hhmm: string, minutesToAdd: number): string {
    const base = (hhmm || '00:00').substring(0, 5);
    const [h, m] = base.split(':').map(n => parseInt(n, 10));
    const total = (h * 60 + m) + minutesToAdd;

    const nh = Math.floor((total % (24 * 60)) / 60);
    const nm = total % 60;

    return `${this.pad2(nh)}:${this.pad2(nm)}`;
  }

  private toISODate(d: Date): string {
    const y = d.getFullYear();
    const m = this.pad2(d.getMonth() + 1);
    const day = this.pad2(d.getDate());
    return `${y}-${m}-${day}`;
  }

  private pad2(n: number): string {
    return n < 10 ? '0' + n : '' + n;
  }

  badgeClass(estado?: any): string {
    const s = (estado || '').toUpperCase();
    switch (s) {
      case 'CONFIRMADA': return 'badge badge-success';
      case 'DISPONIBLE': return 'badge badge-info';
      case 'RESERVADA': return 'badge badge-primary';
      case 'PENDIENTE': return 'badge badge-warning';
      case 'CANCELADA': return 'badge badge-secondary';
      case 'FINALIZADA': return 'badge badge-danger';
      case 'ACTIVA': return 'badge badge-info';
      default: return 'badge badge-light';
    }
  }
}
